import {
  ArrowDown,
  ArrowUp,
  Banknote,
  Bell,
  Building2,
  Calendar,
  Check,
  ChevronDown,
  ChevronsLeftRight,
  ChevronsUpDown,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  EyeOff,
  Filter,
  Folder,
  Globe,
  GraduationCap,
  GripVertical,
  Image,
  LayoutDashboard,
  LayoutTemplate,
  LifeBuoy,
  Link2,
  ListChecks,
  Megaphone,
  Monitor,
  Moon,
  Newspaper,
  Palette,
  PanelLeft,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Settings,
  ShieldAlert,
  ShoppingBag,
  Smartphone,
  Sun,
  Trash2,
  Type,
  Upload,
  Users,
  Wallet,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../cn'

/**
 * İkon sözlüğü — Figma `Icons` sayfasındaki `Icon` bileşen setiyle bire bir
 * aynı adları taşır (Code Connect eşlemesi bu adlar üzerinden kurulur).
 *
 * Panelin kullandığı 6 ad (building-2, list-checks, shield-alert, refresh-cw,
 * moon, sun) YES-121'de Figma setine eklendi: her biri lucide-react'in kendi
 * path verisinden üretildi (path/vector sayıları kaynakla bire bir), aynı
 * çizgi kalınlığı ve `text/secondary` değişken bağıyla. Figma setinde bu
 * altısının dışındaki 16 ad (check, eye, pencil, trash-2, monitor,
 * grip-vertical…) web sitesi düzenleyicisiyle birlikte kullanılmaya başlandı
 * (YES-112) ve buraya eklendi. Sözlük artık Figma setiyle bire bir: 47 ad.
 */
export const ICONS = {
  'layout-dashboard': LayoutDashboard,
  users: Users,
  wallet: Wallet,
  banknote: Banknote,
  megaphone: Megaphone,
  filter: Filter,
  globe: Globe,
  newspaper: Newspaper,
  folder: Folder,
  calendar: Calendar,
  'graduation-cap': GraduationCap,
  'shopping-bag': ShoppingBag,
  'life-buoy': LifeBuoy,
  settings: Settings,
  search: Search,
  bell: Bell,
  'chevron-down': ChevronDown,
  'chevrons-up-down': ChevronsUpDown,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  plus: Plus,
  download: Download,
  upload: Upload,
  ellipsis: Ellipsis,
  'chevrons-left-right': ChevronsLeftRight,
  'building-2': Building2,
  'list-checks': ListChecks,
  'shield-alert': ShieldAlert,
  'refresh-cw': RefreshCw,
  moon: Moon,
  sun: Sun,
  check: Check,
  'external-link': ExternalLink,
  eye: Eye,
  'eye-off': EyeOff,
  'grip-vertical': GripVertical,
  image: Image,
  'layout-template': LayoutTemplate,
  link: Link2,
  monitor: Monitor,
  palette: Palette,
  'panel-left': PanelLeft,
  pencil: Pencil,
  'rotate-ccw': RotateCcw,
  smartphone: Smartphone,
  'trash-2': Trash2,
  type: Type,
} satisfies Record<string, LucideIcon>

export type IconName = keyof typeof ICONS

/**
 * Boyut ölçeği. Serbest sayı yerine birlik: ölçek tip düzeyinde kapalı, yani
 * araya 17px sıkıştırılamıyor.
 *
 * 14 pill · 16 input · 18 nav/buton · 20 başlık · 24 boş durum
 */
export type IconSize = 14 | 16 | 18 | 20 | 24

export type IconProps = {
  name: IconName
  size?: IconSize
  /**
   * Hover animasyonu. Yalnızca **etkileşimli** öğelerde açılır (nav, buton,
   * sekme). Tablo ve liste ikonlarında kapalı kalır: onlarca satırın aynı anda
   * kıpırdaması hem dikkat dağıtır hem gereksiz iş yapar.
   *
   * Animasyon ikonun kendi üzerinde değil, `group` sınıfı taşıyan **kapsayıcı**
   * hover'ında tetiklenir; kullanıcı bağlantının herhangi bir yerine geldiğinde
   * ikon tepki verir.
   */
  animated?: boolean
  /** Varsayılan çizgi rengi. */
  primaryColor?: string
  /** Hover'da geçilen renk. */
  secondaryColor?: string
  className?: string
  'aria-hidden'?: boolean
  'aria-label'?: string
}

/**
 * Tek ikon sarmalayıcısı.
 *
 * Neden `@animated-color-icons/lucide-react` değil: paket 15,6 MB ve 1936
 * dosya, tip tanımı yok, `sideEffects` beyanı yok ve ikonları derlenmemiş JSX
 * olarak yayınlıyor (Vite bağımlılıkları JSX'ten geçirmez). Bize 22 ikon
 * gerekiyor. Aynı davranışı zaten bağımlı olduğumuz `lucide-react` üzerinde
 * birkaç satır CSS ile alıyoruz. Ayrıntı: ADR yok, YES-121 yorumunda.
 */
export function Icon({
  name,
  size = 18,
  animated = false,
  primaryColor = 'var(--ad-text-muted)',
  secondaryColor = 'var(--ad-primary)',
  className,
  ...rest
}: IconProps) {
  const Cizim = ICONS[name]
  return (
    <Cizim
      size={size}
      // Renkler yalnızca **özel değişken** olarak veriliyor; `color` satır içi
      // yazılmıyor. Satır içi stil sınıfları ezdiği için `group-hover:text-…`
      // asla kazanamıyordu: ilk sürümde animasyon sessizce çalışmadı.
      style={{
        ['--ad-icon' as string]: primaryColor,
        ['--ad-icon-hover' as string]: secondaryColor,
      }}
      className={cn(
        'shrink-0 text-[var(--ad-icon)]',
        animated && [
          // `scale`, `transform` degil: Tailwind v4 olcegi ayri CSS ozelligine
          // yaziyor. `transform` yazildiginda renk yumusuyor ama buyume
          // zipliyordu.
          'transition-[color,scale] duration-200 ease-out',
          'group-hover:text-[var(--ad-icon-hover)] group-hover:scale-110',
          // Hareketi azalt tercihinde renk geçişi kalır, hareket kalkar:
          // bilgi taşıyan kısım korunur, rahatsız eden kısım atılır.
          'motion-reduce:transition-none motion-reduce:group-hover:scale-100',
        ],
        className,
      )}
      {...rest}
    />
  )
}
